import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Accordion, AccordionSummary, AccordionDetails,
    Button, Grid, Snackbar, Alert, Dialog, Pagination
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom';
import axios from 'axios';

import BuildingDetailsACsAddAc from "./BuildingDetailsACsAddAC";
const BuildingDetailsACs = () => {
    const { id } = useParams();
    const [acs, setAcs] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [modalOpen, setModalOpen] = useState(false);
    const [editAc, setEditAc] = useState(null);
    const [page, setPage] = useState(1);
    const itemsPerPage = 8;

    const fetchAcs = () => {
        axios.get(`/buildings/${id}/air-conditioners/`)
            .then(res => setAcs(res.data))
            .catch(() => setSnackbar({ open: true, message: 'Failed to fetch air conditioners.', severity: 'error' }));
    };

    useEffect(() => {
        fetchAcs();
    }, [id]);

    const handleModalOpen = () => setModalOpen(true);
    const handleModalClose = () => {
        setModalOpen(false);
        setEditAc(null);
    };

    const handleSuccess = (actionType) => {
        fetchAcs();
        const message = actionType === 'delete'
            ? 'Air conditioner deleted successfully!'
            : editAc
                ? 'Air conditioner updated successfully!'
                : 'Air conditioner added successfully!';
        setSnackbar({ open: true, message, severity: 'success' });
    };

    const handleError = () => {
        setSnackbar({ open: true, message: 'Failed to save air conditioner.', severity: 'error' });
    };

    const handleDelete = (acId) => {
        axios.delete(`/buildings/${id}/air-conditioners/${acId}/`)
            .then(() => handleSuccess('delete'))
            .catch(() => handleError());
    };

    return (
        <Box sx={{ my: 5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Air Conditioners</Typography>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleModalOpen}>
                    Add AC
                </Button>
            </Box>

            {acs
                .slice((page - 1) * itemsPerPage, page * itemsPerPage)
                .map((ac) => (
                    <Accordion
                        key={ac.id}
                        sx={{
                            mb: 2,
                            borderRadius: 2,
                            boxShadow: 'none',
                            border: '1px solid #e0e0e0',
                            '&::before': { display: 'none' }
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ fontWeight: 'bold', flexGrow: 1 }}>{ac.area}</Typography>
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                sx={{ mr: 1 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditAc(ac);
                                    setModalOpen(true);
                                }}
                            >Edit</Button>
                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                sx={{ mr: 2 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(ac.id);
                                }}
                            >Delete</Button>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                {Object.entries(ac).map(([key, value]) => {
                                    if (key === 'id' || key === 'building' || value === null || value === '') return null;

                                    const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

                                    return (
                                        <Grid item xs={12} sm={6} key={key}>
                                            <Typography variant="subtitle2">{label}</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{value}</Typography>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                ))}

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                    count={Math.ceil(acs.length / itemsPerPage)}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    shape="rounded"
                />
            </Box>

            <Dialog open={modalOpen} onClose={handleModalClose}>
                <BuildingDetailsACsAddAc
                    buildingId={id}
                    ac={editAc}
                    onSuccess={handleSuccess}
                    onError={handleError}
                    onClose={handleModalClose}
                />
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BuildingDetailsACs;
